import React, { useEffect, useRef } from "react";
import ScoreBadge from "./ScoreBadge";
import ScoreBreakdown from "./ScoreBreakdown";
import ChangeStatusMenu from "./ChangeStatusMenu";
import BottomSheet from "./BottomSheet";
import { headerView } from "./headerModel.mjs";

/*
 * HEADER: who this is and where we stand. Nothing else (the brief, "Who owns
 * what"; frames 1a, 1c, 1d, 1g, 1h).
 *
 *   Shahar Bahat                                   28px / 800
 *   Co-Founder & CEO · Pluto Security              the company in the ink colour
 *   [Referral · intro email · Dani Avitz]          the source pill: channel, referrer
 *   [Closed won · no signed copy yet ▾]  Client since Sep 30 · day 1  [33 · medium]
 *
 * The position line is the company's, in the board's words or the relationship's,
 * with the days in stage beside it (record/situation.mjs positionLine). Its pill
 * opens Change status… (a popover under the pill; a bottom sheet on the phone).
 * The score badge is featured, small or absent by situation (headerModel.mjs
 * badgeSize), and its breakdown opens right under the line it was clicked on.
 * The Proposed sub-state slot sits at the end of the line at Agreement + legal
 * and Pricing sent, inert by construction.
 *
 * What is NOT here any more: the lifecycle pill and the Lead / Associated switch
 * (People), the details expansion (People and Profile), the booking box (Behind
 * the scenes). The person's lifecycle is about the person; this line is about the
 * deal.
 *
 * Props:
 *   record   the shell's bundle: contact, company, view (position, prominence,
 *            situation), source (sourcePill), score (scoreView), layout,
 *            ui.status (the menu is open), ui.score (the breakdown is open)
 *   ops      ops.openStatus / ops.closeStatus (the pill and its menu),
 *            ops.toggleBreakdown (the badge); the menu and the breakdown take
 *            the rest of ops themselves
 */
export default function RecordHeader({ record, ops }) {
  const { ui } = record;
  const h = headerView(record);
  const phone = record.layout === "phone";
  const popRef = useRef(null);

  // The popover closes on a click anywhere else, as every menu in the app does.
  // A pointer listener, not a key one: Esc is the menu's own layer (useEscape).
  useEffect(() => {
    if (!ui.status || phone) return undefined;
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) ops.closeStatus();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [ui.status, phone, ops]);

  const menu = ui.status && <ChangeStatusMenu record={record} ops={ops} />;

  return (
    <header className="rec-head">
      <h1 className="rec-name">{h.name}</h1>
      {(h.title || h.company) && (
        <div className="rec-titleline">
          {h.title}
          {h.title && h.company ? " · " : ""}
          {h.company && <b>{h.company}</b>}
        </div>
      )}
      {h.source && (
        <div className="rec-pillrow">
          <span className={`rec-source rec-source--${h.source.variant}`}>{h.source.text}</span>
        </div>
      )}
      {h.hasLine && (
        <div className="rec-position">
          {h.pill && (
            <span className="rec-popwrap" ref={popRef}>
              <button
                type="button"
                className={h.pill.className}
                title="Change status…"
                aria-haspopup="menu"
                aria-expanded={!!ui.status}
                onClick={() => ops.openStatus()}
              >
                {h.pill.text} {ui.status ? "▴" : "▾"}
              </button>
              {menu && !phone && <div className="rec-pop">{menu}</div>}
            </span>
          )}
          {h.text && <span className="rec-position__text">{h.text}</span>}
          {h.badge && <ScoreBadge score={record.score} size={h.badge} open={!!ui.score} onClick={ops.toggleBreakdown} />}
          {h.scoredOn && <span className="rec-head__scoredon">{h.scoredOn}</span>}
          {h.subState && (
            // B-008 is Proposed: a dashed chip and its tag, nothing a click can reach.
            <span className="rec-proposed-wrap" aria-label="Sub-state, proposed">
              <span className="rec-substate">Add sub-state</span>
              <span className="rec-proposed">Proposed</span>
            </span>
          )}
        </div>
      )}
      {h.badge && ui.score && <ScoreBreakdown record={record} ops={ops} />}
      {phone && (
        <BottomSheet open={!!menu} title="Change status…" onClose={ops.closeStatus}>
          {menu}
        </BottomSheet>
      )}
    </header>
  );
}
